import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FileText, Shield, Target, RefreshCw, BarChart3, List,
  FileDown, CheckCircle2, Clock, AlertTriangle, ChevronRight,
  Loader2, Eye, Download, Calendar, Globe, Bug, Brain,
  Play, Search, Filter, Zap, ChevronDown, ChevronUp,
  Activity, Users, Network, Key, Crown, Database,
  PieChart, TrendingUp, AlertCircle, Server, X, FolderOpen,
  Plus, Trash2, FileCode, FileSpreadsheet, ExternalLink,
  Building2, File, LayoutGrid, LayoutList
} from 'lucide-react';
import { generatedReports, workspaces, scans } from '../../services/api/client';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useReportGeneration } from '../../contexts/ReportGenerationContext';

// Report types configuration
const REPORT_TYPES = {
  executive: {
    nameKey: 'reports.types.executive',
    descKey: 'reports.typeDescriptions.executive',
    icon: BarChart3,
    color: 'bg-blue-500'
  },
  technical: {
    nameKey: 'reports.types.technical',
    descKey: 'reports.typeDescriptions.technical',
    icon: Shield,
    color: 'bg-green-500'
  },
  vulnerability_list: {
    nameKey: 'reports.types.vulnerability_list',
    descKey: 'reports.typeDescriptions.vulnerability_list',
    icon: List,
    color: 'bg-yellow-500'
  },
  red_team: {
    nameKey: 'reports.types.redteam',
    descKey: 'reports.typeDescriptions.redteam',
    icon: Target,
    color: 'bg-red-500'
  },
  retest: {
    nameKey: 'reports.types.retest',
    descKey: 'reports.typeDescriptions.retest',
    icon: RefreshCw,
    color: 'bg-purple-500'
  },
  multi_campaign: {
    nameKey: 'reports.types.multi_campaign',
    descKey: 'reports.typeDescriptions.multi_campaign',
    icon: TrendingUp,
    color: 'bg-cyan-500'
  },
  active_directory: {
    nameKey: 'reports.types.active_directory',
    descKey: 'reports.typeDescriptions.active_directory',
    icon: Users,
    color: 'bg-indigo-500'
  },
  data_leak: {
    nameKey: 'reports.types.data_leak',
    descKey: 'reports.typeDescriptions.data_leak',
    icon: Database,
    color: 'bg-pink-500'
  },
  attack_surface: {
    nameKey: 'reports.types.attack_surface',
    descKey: 'reports.typeDescriptions.attack_surface',
    icon: Globe,
    color: 'bg-orange-500'
  }
};

// Export formats
const EXPORT_FORMATS = [
  { id: 'pdf', nameKey: 'reports.formats.pdf', icon: FileDown, color: 'text-red-500' },
  { id: 'docx', nameKey: 'reports.formats.docx', icon: FileText, color: 'text-blue-500' },
  { id: 'html', nameKey: 'reports.formats.html', icon: FileCode, color: 'text-orange-500' },
  { id: 'json', nameKey: 'reports.formats.json', icon: File, color: 'text-yellow-500' },
  { id: 'csv', nameKey: 'reports.formats.csv', icon: FileSpreadsheet, color: 'text-green-500' },
  { id: 'jira', nameKey: 'reports.formats.jira', icon: ExternalLink, color: 'text-blue-400' },
  { id: 'servicenow', nameKey: 'reports.formats.servicenow', icon: ExternalLink, color: 'text-emerald-500' },
  { id: 'glpi', nameKey: 'reports.formats.glpi', icon: ExternalLink, color: 'text-purple-500' }
];

// Status badge component
const StatusBadge = ({ status, t }) => {
  const config = {
    pending: { icon: Clock, color: 'bg-gray-500/20 text-gray-400', labelKey: 'reports.generated.status.pending' },
    generating: { icon: Loader2, color: 'bg-blue-500/20 text-blue-400', labelKey: 'reports.generated.status.generating', spin: true },
    completed: { icon: CheckCircle2, color: 'bg-green-500/20 text-green-400', labelKey: 'reports.generated.status.completed' },
    failed: { icon: AlertTriangle, color: 'bg-red-500/20 text-red-400', labelKey: 'reports.generated.status.failed' }
  };

  const { icon: Icon, color, labelKey, spin } = config[status] || config.pending;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''}`} />
      {t(labelKey)}
    </span>
  );
};

// Report card component
const ReportCard = ({ report, onView, onExport, onDelete, onRefresh }) => {
  const { t } = useI18n();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const typeConfig = REPORT_TYPES[report.report_type] || REPORT_TYPES.executive;
  const Icon = typeConfig.icon;
  const typeName = t(typeConfig.nameKey);

  // Don't show subtitle if report name already starts with the type name (avoids redundancy)
  const showTypeSubtitle = !report.name?.toLowerCase().startsWith(typeName.toLowerCase());

  const handleExport = async (format) => {
    setExporting(true);
    try {
      await onExport(report.id, format);
    } finally {
      setExporting(false);
      setShowExportMenu(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-slate-600/50 transition-all">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`${typeConfig.color} p-3 rounded-xl`}>
          <Icon className="w-6 h-6 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-white truncate" title={report.name}>{report.name}</h3>
              {showTypeSubtitle && <p className="text-sm text-slate-400 mt-1">{typeName}</p>}
            </div>
            <StatusBadge status={report.status} t={t} />
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-slate-500">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Calendar className="w-4 h-4" />
              {new Date(report.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            {report.quality_score && (
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <Zap className="w-4 h-4 text-yellow-500" />
                {t('reports.generated.quality.score')}: {Math.round(report.quality_score)}%
              </span>
            )}
            {report.risk_level && (
              <span className={`flex items-center gap-1.5 whitespace-nowrap ${
                report.risk_level === 'critical' ? 'text-red-400' :
                report.risk_level === 'high' ? 'text-orange-400' :
                report.risk_level === 'medium' ? 'text-yellow-400' : 'text-green-400'
              }`}>
                <AlertCircle className="w-4 h-4" />
                {t(`severity.${report.risk_level}`)}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4">
            {report.status === 'completed' && (
              <>
                <button
                  onClick={() => onView(report.id)}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {t('common.view')}
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    disabled={exporting}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
                  >
                    {exporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {t('reports.generated.export')}
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {showExportMenu && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 py-2">
                      {EXPORT_FORMATS.map(format => (
                        <button
                          key={format.id}
                          onClick={() => handleExport(format.id)}
                          className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-3"
                        >
                          <format.icon className={`w-4 h-4 ${format.color}`} />
                          {t(format.nameKey)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {report.status === 'generating' && (
              <button
                onClick={() => onRefresh(report.id)}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {t('common.refresh')}
              </button>
            )}

            {report.status === 'failed' && (
              <button
                onClick={() => onRefresh(report.id)}
                className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {t('errors.retry')}
              </button>
            )}

            <button
              onClick={() => onDelete(report.id)}
              className="px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 rounded-lg text-sm font-medium text-red-400 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create report modal
const CreateReportModal = ({ isOpen, onClose, onSubmit, workspacesList, scansList }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    scan_id: '',
    workspace_id: '',
    report_type: 'executive',
    name: '',
    language: 'fr'
  });
  const [loading, setLoading] = useState(false);
  const [filteredScans, setFilteredScans] = useState([]);

  useEffect(() => {
    if (formData.workspace_id) {
      const filtered = scansList.filter(s => s.workspace_id === formData.workspace_id);
      setFilteredScans(filtered);
    } else {
      setFilteredScans(scansList);
    }
  }, [formData.workspace_id, scansList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        name: formData.name || `${t(REPORT_TYPES[formData.report_type].nameKey)} - ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{t('reports.generated.createNew')}</h2>
            <p className="text-slate-400 text-sm mt-1">{t('reports.generated.subtitle')}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">{t('reports.generated.selectType')}</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(REPORT_TYPES).map(([key, config]) => {
                const typeName = t(config.nameKey);
                const typeDesc = t(config.descKey);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData(p => ({ ...p, report_type: key }))}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      formData.report_type === key
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                    }`}
                  >
                    <div className={`${config.color} w-10 h-10 rounded-lg flex items-center justify-center mb-2`}>
                      <config.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="font-medium text-white text-sm break-words" title={typeName}>{typeName}</div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2" title={typeDesc}>{typeDesc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Workspace Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('reports.generated.selectWorkspace')} ({t('common.optional')})</label>
            <select
              value={formData.workspace_id}
              onChange={(e) => setFormData(p => ({ ...p, workspace_id: e.target.value, scan_id: '' }))}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            >
              <option value="">{t('workspaces.allWorkspaces')}</option>
              {workspacesList.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </select>
          </div>

          {/* Scan Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('reports.generated.selectScan')} *</label>
            <select
              value={formData.scan_id}
              onChange={(e) => setFormData(p => ({ ...p, scan_id: e.target.value }))}
              required
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            >
              <option value="">{t('reports.generated.selectScan')}</option>
              {filteredScans.map(scan => (
                <option key={scan.id} value={scan.id}>
                  {scan.target} - {new Date(scan.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} ({scan.status})
                </option>
              ))}
            </select>
          </div>

          {/* Report Name */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('common.name')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              placeholder={`${t(REPORT_TYPES[formData.report_type].nameKey)} - ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
            />
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('account.profileSection.language')}</label>
            <div className="flex gap-3">
              {[
                { id: 'fr', name: 'French', flag: '🇫🇷' },
                { id: 'en', name: 'English', flag: '🇬🇧' },
                { id: 'de', name: 'Deutsch', flag: '🇩🇪' },
                { id: 'es', name: 'Español', flag: '🇪🇸' }
              ].map(lang => (
                <button
                  key={lang.id}
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, language: lang.id }))}
                  className={`px-4 py-2 rounded-lg border flex items-center gap-2 transition-all ${
                    formData.language === lang.id
                      ? 'border-emerald-500 bg-emerald-500/10 text-white'
                      : 'border-slate-700 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span className="text-sm">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.scan_id}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('reports.generated.generating')}
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  {t('reports.generateReport')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main component
const GeneratedReportsList = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { activeWorkspaceId } = useWorkspace();
  const { trackReport, registerOnComplete } = useReportGeneration();

  const [reports, setReports] = useState([]);
  const [workspacesList, setWorkspacesList] = useState([]);
  const [scansList, setScansList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'flat'
  const [activeCategory, setActiveCategory] = useState('all');

  // Fetch data - show all company reports in the library (no workspace filter)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const reportParams = { limit: 100 };

      const [reportsData, wsData, scansData] = await Promise.all([
        generatedReports.list(reportParams),
        workspaces.list(0, 100),
        scans.list(0, 100, null)
      ]);

      setReports(reportsData.reports || reportsData || []);
      setWorkspacesList(wsData.workspaces || wsData || []);
      setScansList(scansData.scans || scansData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh quand un rapport en cours de suivi termine
  useEffect(() => {
    return registerOnComplete(() => fetchData());
  }, [registerOnComplete, fetchData]);

  // Create report handler
  const handleCreateReport = async (data) => {
    try {
      const response = await generatedReports.create(data);
      const reportName = data.report_name || data.name || `Rapport ${data.report_type || ''}`.trim();
      if (response?.id) {
        trackReport(response.id, reportName, data.report_type);
      }
      fetchData();
    } catch (err) {
      console.error('Error creating report:', err);
      throw err;
    }
  };

  // Export handler
  const handleExport = async (reportId, format) => {
    try {
      // First trigger export generation
      await generatedReports.export(reportId, format);

      // Then download the file
      const blob = await generatedReports.download(reportId, format);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
    }
  };

  // Delete handler
  const handleDelete = async (reportId) => {
    if (!window.confirm(t('reports.generated.confirmDelete'))) return;

    try {
      await generatedReports.delete(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (err) {
      console.error('Error deleting report:', err);
    }
  };

  // View handler
  const handleView = (reportId) => {
    navigate(`/reports/generated/${reportId}`);
  };

  // Refresh handler
  const handleRefresh = async (reportId) => {
    try {
      const status = await generatedReports.getStatus(reportId);
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, ...status } : r));
    } catch (err) {
      console.error('Error refreshing report:', err);
    }
  };

  // Filter reports
  const filteredReports = reports.filter(report => {
    if (searchTerm && !report.name?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterType !== 'all' && report.report_type !== filterType) {
      return false;
    }
    if (filterStatus !== 'all' && report.status !== filterStatus) {
      return false;
    }
    return true;
  });

  // Stats
  const stats = {
    total: reports.length,
    completed: reports.filter(r => r.status === 'completed').length,
    generating: reports.filter(r => r.status === 'generating').length,
    failed: reports.filter(r => r.status === 'failed').length
  };

  // Group reports by category for grouped view
  const groupedReports = useMemo(() => {
    const groups = {};
    Object.keys(REPORT_TYPES).forEach(type => {
      groups[type] = filteredReports.filter(r => r.report_type === type);
    });
    return groups;
  }, [filteredReports]);

  // Count reports per category
  const categoryCounts = useMemo(() => {
    const counts = { all: reports.length };
    Object.keys(REPORT_TYPES).forEach(type => {
      counts[type] = reports.filter(r => r.report_type === type).length;
    });
    return counts;
  }, [reports]);

  if (loading && reports.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Brain className="w-8 h-8 text-emerald-500" />
              {t('reports.generated.title')}
            </h1>
            <p className="text-slate-400 mt-2">
              {t('reports.generated.subtitle')}
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-medium text-white transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('reports.newReport')}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-sm text-slate-400">{t('reports.generated.totalReports')}</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="text-2xl font-bold text-green-400">{stats.completed}</div>
            <div className="text-sm text-slate-400">{t('reports.generated.completedReports')}</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-400">{stats.generating}</div>
            <div className="text-sm text-slate-400">{t('reports.generated.status.generating')}</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <div className="text-2xl font-bold text-red-400">{stats.failed}</div>
            <div className="text-sm text-slate-400">{t('reports.generated.status.failed')}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('common.search')}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>
          </div>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-emerald-500 outline-none"
          >
            <option value="all">{t('common.allTypes')}</option>
            {Object.entries(REPORT_TYPES).map(([key, config]) => (
              <option key={key} value={key}>{t(config.nameKey)}</option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-emerald-500 outline-none"
          >
            <option value="all">{t('common.allStatuses')}</option>
            <option value="completed">{t('reports.generated.status.completed')}</option>
            <option value="generating">{t('reports.generated.status.generating')}</option>
            <option value="pending">{t('reports.generated.status.pending')}</option>
            <option value="failed">{t('reports.generated.status.failed')}</option>
          </select>

          {/* View mode toggle */}
          <div className="flex gap-1 bg-slate-900 border border-slate-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grouped')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grouped'
                  ? 'bg-emerald-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
              title={t('common.view')}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'flat'
                  ? 'bg-emerald-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
              title={t('common.view')}
            >
              <LayoutList className="w-4 h-4" />
            </button>
          </div>

          {/* Refresh */}
          <button
            onClick={fetchData}
            className="p-2.5 bg-slate-900 border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Category Tabs - Only show in grouped view */}
      {viewMode === 'grouped' && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-slate-700">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap shrink-0 ${
              activeCategory === 'all'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            {t('common.all')} ({categoryCounts.all})
          </button>
          {Object.entries(REPORT_TYPES).map(([key, config]) => {
            const count = categoryCounts[key] || 0;
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap shrink-0 ${
                  activeCategory === key
                    ? `${config.color} text-white`
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t(config.nameKey)} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Reports list */}
      {filteredReports.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-12 text-center">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('reports.generated.noReports')}</h3>
          <p className="text-slate-400 mb-6">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? t('common.noData')
              : t('reports.generated.noReportsDesc')}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-medium text-white transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('reports.generated.create')}
          </button>
        </div>
      ) : viewMode === 'grouped' && activeCategory !== 'all' ? (
        /* Grouped view - specific category */
        <div className="space-y-4">
          {groupedReports[activeCategory]?.length > 0 ? (
            groupedReports[activeCategory].map(report => (
              <ReportCard
                key={report.id}
                report={report}
                onView={handleView}
                onExport={handleExport}
                onDelete={handleDelete}
                onRefresh={handleRefresh}
              />
            ))
          ) : (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
              <p className="text-slate-400">{t('reports.generated.noReports')}</p>
            </div>
          )}
        </div>
      ) : viewMode === 'grouped' && activeCategory === 'all' ? (
        /* Grouped view - all categories with headers */
        <div className="space-y-8">
          {Object.entries(REPORT_TYPES).map(([type, config]) => {
            const typeReports = groupedReports[type] || [];
            if (typeReports.length === 0) return null;
            const Icon = config.icon;
            return (
              <div key={type}>
                <h3 className="flex items-center gap-3 text-lg font-semibold text-white mb-4">
                  <div className={`${config.color} p-2.5 rounded-lg`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {t(config.nameKey)}
                  <span className="text-sm text-slate-400 font-normal">
                    ({typeReports.length})
                  </span>
                </h3>
                <div className="space-y-3 pl-4 border-l-2 border-slate-700">
                  {typeReports.map(report => (
                    <ReportCard
                      key={report.id}
                      report={report}
                      onView={handleView}
                      onExport={handleExport}
                      onDelete={handleDelete}
                      onRefresh={handleRefresh}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Flat view */
        <div className="space-y-4">
          {filteredReports.map(report => (
            <ReportCard
              key={report.id}
              report={report}
              onView={handleView}
              onExport={handleExport}
              onDelete={handleDelete}
              onRefresh={handleRefresh}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      <CreateReportModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateReport}
        workspacesList={workspacesList}
        scansList={scansList}
      />
    </div>
  );
};

export default GeneratedReportsList;
